import { Box, Flex } from "@rebass/grid";
import Spacer, {
  ESpacerWeight,
} from "@rentiohq/shared-frontend/dist/components/components/Spacer";
import { useQueryParams } from "@rentiohq/shared-frontend/dist/hooks/useQueryParams";
import logger from "@rentiohq/shared-frontend/dist/logger";
import * as authHooks from "@rentiohq/shared-frontend/dist/redux/auth/auth.hooks";
import * as contactApi from "@rentiohq/shared-frontend/dist/redux/contact/contact.api";
import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import * as paymentApi from "@rentiohq/shared-frontend/dist/redux/payment/payment.api";
import { IContact } from "@rentiohq/shared-frontend/dist/types/contact.types";
import {
  EMangoPersonType,
  EMangoScaStatus,
  EMissingKYCStatus,
  IBrokerKybInfo,
  IMissingKYC,
} from "@rentiohq/shared-frontend/dist/types/payment.types";
import { showAlert } from "@rentiohq/shared-frontend/dist/utils/alert/alert.utils";
import { confirm } from "@rentiohq/shared-frontend/dist/utils/confirm.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import {
  canPerformKyc as canPerformKycForStatus,
  getMissingKycAppearance,
  getMissingKycRenewalPendingReason,
} from "@rentiohq/shared-frontend/dist/utils/kyc/missingKyc.utils";
import {
  ActionCard,
  Button,
  Card,
  DisplayText,
  ESpacings,
  Grid,
  Icon,
  Loading,
  Modal,
  RentioInternalRenderer,
} from "@rentiohq/web-shared/dist/components";
import { useToggle } from "@rentiohq/web-shared/dist/hooks/useToggle";
import { toast } from "@rentiohq/web-shared/dist/managers/Toast";
import { TAppearance } from "@rentiohq/web-shared/dist/types";
import utils, { i18n } from "@rentiohq/web-shared/dist/utils";
import { AxiosError } from "axios";
import sortBy from "lodash/sortBy";
import React from "react";
import { useNavigate } from "react-router-dom";
import { StringParam } from "serialize-query-params";
import {
  getKybReturnUrl,
  handleKybSession,
} from "../../../../../../forms/kycVerification/schema.kycVerification.actions";
import * as t from "../../../../../../services/translationService";
import {
  fetchKybInfoAfterReturn,
  getKybOwnerGroups,
  isKybSubmitted,
} from "../../../../../../utils/kyb.utils";
import CollectionFollowUpKycModal from "./CollectionFollowUpKycModal";
import { CollectionFollowUpScaModal } from "./CollectionFollowUpScaModal";

const KYCS_TO_SHOW_WHEN_COLLAPSED = 6;

interface IProps {
  items: IMissingKYC[];
  isFetching: boolean;
  refetch: () => void;
}

const CollectionFollowUp = (props: IProps) => {
  const { items, isFetching, refetch } = props;
  const { isBroker } = authHooks.useSelf();

  const navigate = useNavigate();

  const [queryParamValue, setQueryParamValue] = useQueryParams({
    scaCallbackContactId: StringParam,
    kybCallbackContactId: StringParam,
    controlStatus: StringParam,
  });

  // State
  const [showAllMissingKycs, toggleShowAllMissingKycs] = useToggle(false);
  const [kycContactToPerform, setKycContactToPerform] = React.useState<{
    contactId: number;
    bankAccountId: number;
  }>();
  const [scaContactToPerform, setScaContactToPerform] = React.useState<{
    contactId: number;
    bankAccountId: number;
    dismissable: boolean;
  }>();
  // null = the KYB info could not be read, so the card falls back to the regular KYC/SCA steps
  const [kybInfoByContactId, setKybInfoByContactId] = React.useState<
    Record<number, IBrokerKybInfo | null>
  >({});
  const [resumingKybContactId, setResumingKybContactId] =
    React.useState<number>();

  // DEV-2801: a company's KYB is only done once Mangopay made it REGULAR (status approved); until then the card
  // keeps the KYB as its step, so read the hosted session of every legal user that is not approved yet
  const fetchKybInfo = React.useCallback(async (contactId: number) => {
    try {
      const { data } = await contactApi.getKycIdv(contactId);
      setKybInfoByContactId(previous => ({ ...previous, [contactId]: data }));

      return data;
    } catch (unknownError) {
      logger.logError({ error: unknownError as Error });
      setKybInfoByContactId(previous => ({ ...previous, [contactId]: null }));

      return undefined;
    }
  }, []);

  // Only a company can be verified through a KYB here: a private person never is, so the list does not ask
  // serverless (and Mangopay behind it) about every payee, an office can have hundreds open
  const pendingKybContactIds = isBroker
    ? items
        .filter(
          item =>
            item.contact?.isCompany &&
            item.status !== EMissingKYCStatus.Approved,
        )
        .map(item => item.contact.id)
        .join(",")
    : "";

  React.useEffect(() => {
    pendingKybContactIds
      .split(",")
      .filter(Boolean)
      .forEach(contactId => fetchKybInfo(Number(contactId)));
  }, [pendingKybContactIds, fetchKybInfo]);

  // Event handlers
  const resendKYCReminder = async ({ contact }: { contact: IContact }) => {
    try {
      await paymentApi.sendKYCReminder(contact.id);
      toast({
        heading: t.contactResendKYCRegistrationSentSuccess({
          name: contact.firstname,
        }),
      });
    } catch (unknownError) {
      const error = unknownError as any;
      logger.logError({ error });

      toast({
        heading: t.contactResendKYCRegistrationSentFail({
          name: contact.firstname,
        }),
        variation: "error",
      });
    }
  };

  const handleKyc = async (item: IMissingKYC) => {
    if (!item.bankAccountId) {
      showAlert({
        type: "error",
        message: "Bank account not found",
      });

      return;
    }

    setKycContactToPerform({
      contactId: item.contact.id,
      bankAccountId: item.bankAccountId,
    });
  };

  const handleSca = React.useCallback(
    async (params: { item: IMissingKYC; dismissable: boolean }) => {
      const { item, dismissable } = params;

      if (!item.bankAccountId) {
        showAlert({
          type: "error",
          message: "Bank account not found",
        });

        return;
      }

      setScaContactToPerform({
        contactId: item.contact.id,
        bankAccountId: item.bankAccountId,
        dismissable,
      });
    },
    [],
  );

  // Back from Mangopay's hosted page: once the session is submitted, SCA is the next step for this contact
  const kybCallbackContactId = queryParamValue.kybCallbackContactId
    ? Number(queryParamValue.kybCallbackContactId)
    : undefined;
  const kybCallbackItem = kybCallbackContactId
    ? items.find(item => item.contact?.id === kybCallbackContactId)
    : undefined;

  // The param is cleared right away, but until the URL updates a re-render must not handle it twice
  const handledKybCallbackContactId = React.useRef<number>();

  React.useEffect(() => {
    if (
      !kybCallbackContactId ||
      !kybCallbackItem ||
      handledKybCallbackContactId.current === kybCallbackContactId
    ) {
      return;
    }
    handledKybCallbackContactId.current = kybCallbackContactId;

    setQueryParamValue({ kybCallbackContactId: undefined });
    refetch();

    fetchKybInfoAfterReturn(() => fetchKybInfo(kybCallbackContactId)).then(
      kybInfo => {
        const isSubmitted = isKybSubmitted(kybInfo);
        const canPerformSca = [
          EMangoScaStatus.NotEnrolled,
          EMangoScaStatus.Failed,
        ].includes(kybCallbackItem.scaStatus);

        if (isSubmitted && canPerformSca) {
          handleSca({ item: kybCallbackItem, dismissable: true });
        }
      },
    );
  }, [
    kybCallbackContactId,
    kybCallbackItem,
    fetchKybInfo,
    handleSca,
    refetch,
    setQueryParamValue,
  ]);

  // The open hosted session is returned again by POST broker-kyc-idv, so the broker continues on the same link
  const handleResumeKyb = async (item: IMissingKYC) => {
    setResumingKybContactId(item.contact.id);

    try {
      const { data } = await contactApi.startKycIdv(item.contact.id, {
        returnUrl: getKybReturnUrl(item.contact.id),
      });
      handleKybSession(data);
    } catch (unknownError) {
      logger.logError({ error: unknownError as Error });
      showAlert({ type: "error", message: getLocalizedText("general.error") });
    } finally {
      setResumingKybContactId(undefined);
    }
  };

  const handleExtraInfoClick = () => {
    utils.general.openWindow(
      utils.i18n.t("payments.collection_follow_up.url.dashboard"),
    );
  };

  const handleClose = () => {
    setKycContactToPerform(undefined);
    setScaContactToPerform(undefined);
  };

  const handleKycSuccess = async () => {
    const item = items.find(
      item => item.contact.id === kycContactToPerform?.contactId,
    );
    if (!item) {
      handleClose();
      refetch();

      return;
    }

    const canPerformSca = [
      EMangoScaStatus.NotEnrolled,
      EMangoScaStatus.Failed,
    ].includes(item.scaStatus);
    if (!canPerformSca) {
      handleClose();
      refetch();

      return;
    }

    handleClose();
    await handleSca({ item, dismissable: false });
  };

  const handleKycError = async (error: Error) => {
    const item = items.find(
      item => item.contact.id === kycContactToPerform?.contactId,
    );
    if (!item) {
      handleClose();
      refetch();

      return;
    }

    const axiosError = error as AxiosError;
    if (axiosError.isAxiosError) {
      const { mangoErrorType } = axiosError.response?.data;
      if (mangoErrorType === "fraud_policy") {
        confirm({
          type: "error",
          title: getLocalizedText("kyc.error.mango_fraud_policy.title"),
          info: i18n.tm("kyc.error.mango_fraud_policy.content"),
          secondaryAction: {
            title: getLocalizedText("system.back"),
          },
          primaryActions: [
            {
              title: getLocalizedText("contact.edit.title"),
              onPress: () => {
                navigate(`/contacts/${item.contact.id}`);
              },
            },
          ],
        });
      }
    }
  };

  const handleScaSuccess = async () => {
    handleClose();
    refetch();
  };

  // Render
  return (
    <>
      <Card>
        <Flex mb={ESpacings.base} alignItems="center">
          <DisplayText space="none" size="medium">
            {t.paymentsCollectionFollowUpHeading()}{" "}
          </DisplayText>

          <Box ml={ESpacings.tight}>
            <Button onClick={handleExtraInfoClick} appearance="plain">
              <Icon source="informationCircle" size="small" />
            </Button>
          </Box>

          {isFetching && (
            <Box ml={ESpacings.tight}>
              <Loading asDots={true} />
            </Box>
          )}
        </Flex>

        <Grid>
          {sortBy(items, "status")
            .slice(
              0,
              showAllMissingKycs ? items.length : KYCS_TO_SHOW_WHEN_COLLAPSED,
            )
            .map((item, index) => {
              const handleKYCReminderClick = () => {
                resendKYCReminder({ contact: item.contact });
              };

              const canPerformKyc = canPerformKycForStatus(item.status);
              const canPerformSca = [
                EMangoScaStatus.NotEnrolled,
                EMangoScaStatus.Failed,
              ].includes(item.scaStatus);

              const kybInfo = item.contact
                ? kybInfoByContactId[item.contact.id]
                : undefined;
              // Until it is known whether a requested contact has a KYB open, its SCA button would only flash
              const isKybInfoLoading =
                isBroker &&
                !!item.contact?.isCompany &&
                item.status === EMissingKYCStatus.Requested &&
                kybInfo === undefined;
              const isOpenKyb =
                kybInfo?.personType === EMangoPersonType.Legal &&
                item.status === EMissingKYCStatus.Requested;
              const isKybSubmittedForItem =
                isOpenKyb && isKybSubmitted(kybInfo);
              // Started but not submitted (the session is PENDING): the KYB stays the step, not SCA
              const canResumeKyb = isOpenKyb && !isKybSubmittedForItem;
              const { refused: refusedKybOwners, pending: pendingKybOwners } =
                getKybOwnerGroups(isOpenKyb ? kybInfo?.kybOwners : []);

              const actions = [];

              if (canPerformKyc) {
                actions.push({
                  content: t.contactResendKYCRegistrationAction(),
                  onClick: handleKYCReminderClick,
                });
              }

              if (item.contact) {
                actions.push([
                  {
                    content: t.contactViewContactAction(),
                    url: `/contacts/${item.contact.id}`,
                  },
                ]);
              }

              const primaryActions = [];
              if (canPerformKyc) {
                primaryActions.push({
                  content: getLocalizedText(
                    "form.kyc_receiver_as_broker.cta.start",
                  ),
                  onClick: () => handleKyc(item),
                  color: "red",
                  appearance: "outline",
                });
              } else if (canResumeKyb) {
                primaryActions.push({
                  content: getLocalizedText(
                    "form.kyb_receiver_as_broker.cta.resume",
                  ),
                  onClick: () => handleResumeKyb(item),
                  isSubmitting: resumingKybContactId === item.contact.id,
                  color: "red",
                  appearance: "outline",
                });
              } else if (canPerformSca && !isKybInfoLoading) {
                primaryActions.push({
                  content: getLocalizedText(
                    "form.sca_receiver_as_broker.cta.start",
                  ),
                  onClick: () => handleSca({ item, dismissable: true }),
                  color: "red",
                  appearance: "outline",
                });
              }

              const renderReason = () => {
                if (
                  item.status === EMissingKYCStatus.Failed &&
                  item.errorMessage
                ) {
                  const errorKey =
                    `payments.kyc_failed.reason.${item.errorMessage}`.toLowerCase();
                  const ctaKeyTitle =
                    `payments.kyc_failed.reason.${item.errorMessage}.cta.title`.toLowerCase();
                  const ctaKeyUrl =
                    `payments.kyc_failed.reason.${item.errorMessage}.cta.url`.toLowerCase();

                  const hasLocalizedUrl =
                    getLocalizedText(ctaKeyUrl) !== ctaKeyUrl;

                  return (
                    <>
                      {getLocalizedText(errorKey)}
                      {hasLocalizedUrl && (
                        <div>
                          <Spacer weight={ESpacerWeight.W02} />

                          <Button
                            appearance="link"
                            onClick={() => {
                              setTimeout(() => {
                                window.open(
                                  getLocalizedText(ctaKeyUrl),
                                  "_blank",
                                );
                              }, 0);
                            }}
                          >
                            {getLocalizedText(ctaKeyTitle)}
                          </Button>
                        </div>
                      )}
                    </>
                  );
                }

                if (item.status === EMissingKYCStatus.RenewalPending) {
                  return getMissingKycRenewalPendingReason(
                    item.kycRenewalDeadline,
                  );
                }

                if (refusedKybOwners.length > 0) {
                  return getLocalizedText(
                    "payments.collection.follow_up.kyb_reason.owners_refused",
                    {
                      names: refusedKybOwners
                        .map(owner => owner.name)
                        .join(", "),
                    },
                  );
                }

                if (pendingKybOwners.length > 0) {
                  return getLocalizedText(
                    "payments.collection.follow_up.kyb_reason.owners",
                    {
                      names: pendingKybOwners
                        .map(owner => owner.name)
                        .join(", "),
                    },
                  );
                }

                if (canResumeKyb) {
                  return getLocalizedText(
                    "payments.collection.follow_up.kyb_reason.pending",
                  );
                }

                if (isKybSubmittedForItem && !canPerformSca) {
                  return getLocalizedText(
                    "payments.collection.follow_up.kyb_reason.review",
                  );
                }

                if (
                  [EMissingKYCStatus.None, EMissingKYCStatus.New].includes(
                    item.status,
                  )
                ) {
                  return t.paymentsCollectionFollowUpReason({
                    reason: item.status,
                  });
                }

                if (
                  [
                    EMangoScaStatus.NotEnrolled,
                    EMangoScaStatus.Failed,
                  ].includes(item.scaStatus)
                ) {
                  return getLocalizedText(
                    `payments.collection.follow_up.sca_reason.${item.scaStatus}`.toLowerCase(),
                  );
                }

                return t.paymentsCollectionFollowUpReason({
                  reason: item.status,
                });
              };

              const kycAppearance: TAppearance = getMissingKycAppearance(
                item.status,
              );

              let scaAppearance: TAppearance = "error";
              switch (item.scaStatus) {
                case EMangoScaStatus.Enrolled:
                  scaAppearance = "success";
                  break;

                case EMangoScaStatus.NotEnrolled:
                case EMangoScaStatus.Failed:
                default:
                  break;
              }

              const appearances: TAppearance[] = [
                "error",
                "warning",
                "info",
                "success",
              ];
              const worstAppearance = appearances.find(
                appearance =>
                  appearance === kycAppearance || appearance === scaAppearance,
              );

              return (
                <Grid.Item key={index} width={[1, 1, 1 / 3]}>
                  <ActionCard
                    appearance={worstAppearance}
                    heading={
                      <Flex alignItems="center">
                        {item.contact && (
                          <Box flex={1} mr={ESpacings.tight}>
                            <DisplayText size="small">
                              {getName(item.contact)}
                            </DisplayText>
                          </Box>
                        )}
                      </Flex>
                    }
                    actions={actions}
                    reason={renderReason()}
                    // @ts-ignore
                    primaryActions={primaryActions}
                  />
                </Grid.Item>
              );
            })}
        </Grid>

        {items.length > KYCS_TO_SHOW_WHEN_COLLAPSED && (
          <>
            <Spacer weight={ESpacerWeight.W12} />

            {!showAllMissingKycs && (
              <Button appearance="link" onClick={toggleShowAllMissingKycs}>
                {`${getLocalizedText("system.show_all")} (${items.length})`}
              </Button>
            )}

            {showAllMissingKycs && (
              <Button appearance="link" onClick={toggleShowAllMissingKycs}>
                {getLocalizedText("system.show_less")}
              </Button>
            )}
          </>
        )}
      </Card>

      {kycContactToPerform && (
        <CollectionFollowUpKycModal
          contactId={kycContactToPerform.contactId}
          bankAccountId={kycContactToPerform.bankAccountId}
          onSuccess={handleKycSuccess}
          onError={handleKycError}
          onClose={handleClose}
          asBroker={isBroker}
        />
      )}

      {scaContactToPerform && (
        <CollectionFollowUpScaModal
          contactId={scaContactToPerform.contactId}
          bankAccountId={scaContactToPerform.bankAccountId}
          dismissable={scaContactToPerform.dismissable}
          onSuccess={handleScaSuccess}
          onClose={handleClose}
        />
      )}

      {queryParamValue.scaCallbackContactId && (
        <Modal
          onClose={() => {
            setQueryParamValue({
              scaCallbackContactId: undefined,
              controlStatus: undefined,
            });
            refetch();
          }}
          actions={[
            {
              content: getLocalizedText("system.close"),
              appearance: "primary",
              size: "small",
              onClick: () => {
                setQueryParamValue({
                  scaCallbackContactId: undefined,
                  controlStatus: undefined,
                });
                refetch();
              },
            },
          ]}
          heading={getLocalizedText(
            "payments.collection.follow_up.sca.callback.title",
          )}
          hasDismiss={true}
          shouldCloseOnOverlayClick={true}
          width="large"
        >
          {getLocalizedText("payments.collection.follow_up.sca.callback.info")}

          <RentioInternalRenderer items={queryParamValue} />
        </Modal>
      )}
    </>
  );
};

// eslint-disable-next-line import/no-default-export
export default React.memo(CollectionFollowUp);
